import type { EventCollector } from "../EventCollector";
import { EventForMessages } from "../utils/TagManager";
import { EventIdentity } from "../pixels/EventIdentity";

export { LeadCreationStructure };

interface DateInfo {
  time_zone: string;
  time_zone_offset: string;
  created_at: string;
  created_at_integer: number;
}

interface UrlInfo {
  url: string;
}

type MergedLeadData<T extends EventForMessages> = T &
  DateInfo &
  UrlInfo & {
    event_type: string;
    infoproduct_id: number;
    team_id: number;
    // Identidad canónica del evento de conversión (ver EventIdentity): la
    // acuña do() antes del POST. Opcional: sin email ni píxeles sigue
    // existiendo, pero un mensaje viejo sin ella es perfectamente válido.
    event_id?: string;
  };

class LeadCreationStructure {
  private _eventType = "LeadCreation";
  private _eventCollector: EventCollector;

  constructor(eventCollector: EventCollector) {
    this._eventCollector = eventCollector;
  }

  get eventType() {
    return this._eventType;
  }

  getTimezoneOffset(): string {
    const offset = new Date().getTimezoneOffset();
    const o = Math.abs(offset);

    return (
      (offset < 0 ? "+" : "-") +
      ("00" + Math.floor(o / 60)).slice(-2) +
      ":" +
      ("00" + (o % 60)).slice(-2)
    );
  }

  getCurrentDate(): DateInfo {
    const timestamp = new Date();
    const datetime =
      timestamp.getUTCFullYear() +
      "-" +
      ("00" + (timestamp.getUTCMonth() + 1)).slice(-2) +
      "-" +
      ("00" + timestamp.getUTCDate()).slice(-2) +
      " " +
      ("00" + timestamp.getUTCHours()).slice(-2) +
      ":" +
      ("00" + timestamp.getUTCMinutes()).slice(-2) +
      ":" +
      ("00" + timestamp.getUTCSeconds()).slice(-2);
    return {
      time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      time_zone_offset: this.getTimezoneOffset(),
      created_at: datetime,
      created_at_integer: timestamp.valueOf(),
    };
  }

  getCurrentUrl(): UrlInfo {
    return {
      url: location.href,
    };
  }

  mergeData<T extends EventForMessages>(data: T): MergedLeadData<T> {
    const finalDataObject = {
      ...data,
      ...this._eventCollector.user.getAdditionalDataToMessage(),
      ...this._eventCollector.tracking.getAdditionalDataToMessage(),
      // Ad click history (all captured click IDs): always attached to the
      // optin as the durable, first-party transmission path — the backend
      // links them to the visitor_id and snapshots them on the optin.
      // JSON-serialized: the CRM message pipeline casts values to string.
      ...this._eventCollector.clickIds.getSerializedDataForLead(),
      ...this._eventCollector.experiment.getAdditionalDataToMessage(),
      ...this.getCurrentDate(),
      ...this.getCurrentUrl(),
      ...{ event_type: this.eventType },
      ...{ infoproduct_id: Number(this._eventCollector.crmInfoproductId) },
      ...{ team_id: Number(this._eventCollector.crmTeamId) },
      // `event_id`: identidad canónica compartida con el píxel de navegador y
      // con la CAPI. Viaja en el cuerpo del mensaje (`body: true` en el
      // schema del lead, porque los 10 MessageAttributes de SQS ya están
      // casi llenos) → consumer → `user_optin.browser_event_id` → webhook
      // leadOptin → `conversion_event.event_id` en marketing, donde la red
      // deduplica contra la copia que mandó el navegador.
      ...this.getEventIdentity(data),
    };

    for (const property in finalDataObject) {
      if (finalDataObject[property] === "") {
        delete finalDataObject[property];
      }
    }

    return finalDataObject as MergedLeadData<T>;
  }

  // El id ya viene normalizado de do() (misma cadena para píxel, dataLayer y
  // mensaje); aquí se vuelve a pasar por la MISMA función por si el mensaje se
  // construye desde otro camino, para que un valor raro de un plugin nunca
  // haga fallar la validación del lambda y con ella el lead.
  private getEventIdentity<T extends EventForMessages>(
    data: T,
  ): Record<string, string> {
    const candidate = EventIdentity.normalize(
      (data as unknown as Record<string, unknown>)["event_id"],
    );
    if (candidate === "") {
      return {};
    }
    return { event_id: candidate };
  }

  generateMessage<T extends EventForMessages>(data: T): MergedLeadData<T> {
    return this.mergeData(data);
  }

  getEndpoint(): string {
    return this._eventCollector.config.CFG_ENDPOINTS[
      this.eventType + "Endpoint"
    ];
  }
}
